import { View } from "react-native";
import {
  Baby,
  Beaker,
  BookOpen,
  ClipboardList,
  Dna,
  Hourglass,
  Pill,
  Sparkles,
  Syringe,
  Target,
  Zap,
  ClipboardCheck,
  type LucideIcon,
} from "lucide-react-native";
import type {
  StepPerspective,
  TreatmentStepConfig,
} from "@src/packages/homepage/sections/TreatmentProgressModal";
import {
  buildStepExplainer,
  resolveStepExplainerId,
  stepExplainerContextForStep,
  type StepExplainer,
} from "@louise/care/education";
import { StepExplainerPanels } from "@louise/care/education/StepExplainerPanels";
import type { CycleType } from "@src/packages/cycles/hooks/useCycles";
import { useTranslations } from "@src/packages/i18n";
import type { TranslationFunction } from "@src/packages/i18n";
import { usePhases } from "@src/packages/patients/hooks/usePhases";
import { useRetrievalClinicInstructions } from "@src/packages/cycles/hooks/useRetrievalClinicInstructions";

/** What the hub knows about the cycle, to pick and word a step's explainer. */
export type CycleContentContext = {
  cycleId?: string | null;
  cycleType?: CycleType | null;
  /** Frozen eggs drive this IVF or ICSI cycle. */
  usingFrozenSpecimens?: boolean;
  /** See `StepExplainerContext.cycleHadTriggerShot`. */
  cycleHadTriggerShot?: boolean;
};

const STEP_ICONS: Record<string, LucideIcon> = {
  pre_treatment: ClipboardList,
  stimulation: Syringe,
  protocol: Syringe,
  trigger_shot: Zap,
  egg_retrieval: Sparkles,
  freezing: ClipboardCheck,
  fertilization: Beaker,
  embryo_development: Beaker,
  transfer: Baby,
  // The hourglass belongs to the wait, which is the outcome step now. Luteal
  // support is the medication.
  luteal_phase: Pill,
  outcome: Hourglass,
  sperm_collection: Beaker,
  insemination: Target,
  screening: ClipboardList,
  analysis: Beaker,
  pgt_testing: Dna,
};

export function getMoonHubStepIcon(step: TreatmentStepConfig): LucideIcon {
  return STEP_ICONS[step.id] ?? STEP_ICONS[step.contentType] ?? BookOpen;
}

/**
 * The explainer for the step tab she selected, current or not. The text comes
 * only from the shared step explainers, the same source Home reads, so the two
 * surfaces cannot explain one step two ways.
 */
function getMoonHubStepExplainer({
  t,
  step,
  perspective,
  cycleContext,
  retrievalClinicInstructions,
}: {
  t: TranslationFunction;
  step: TreatmentStepConfig;
  perspective: StepPerspective;
  cycleContext?: CycleContentContext;
  retrievalClinicInstructions: string | null;
}): StepExplainer | null {
  const context = stepExplainerContextForStep({
    step,
    // The hub builds its steps as IVF when the cycle type is unknown, so the
    // explainer follows the same steps.
    cycleType: cycleContext?.cycleType ?? "ivf",
    perspective,
    usingFrozenOocytes: cycleContext?.usingFrozenSpecimens === true,
    cycleHadTriggerShot: cycleContext?.cycleHadTriggerShot === true,
  });
  const id = resolveStepExplainerId(context);
  return id
    ? buildStepExplainer(t, id, { ...context, retrievalClinicInstructions })
    : null;
}

export function MoonHubGuidanceCarousel({
  step,
  perspective,
  cycleContext,
  measureKey,
}: {
  step: TreatmentStepConfig;
  perspective: StepPerspective;
  cycleContext?: CycleContentContext;
  measureKey: string;
}) {
  const t = useTranslations();
  // The hub card already reads these phases; the query is shared, not repeated.
  const { data: phases = [] } = usePhases(cycleContext?.cycleId);
  const retrievalClinicInstructions = useRetrievalClinicInstructions(phases);
  const explainer = getMoonHubStepExplainer({
    t,
    step,
    perspective,
    cycleContext,
    retrievalClinicInstructions,
  });

  if (!explainer) return null;

  return (
    <View style={{ paddingTop: 14, paddingBottom: 4 }}>
      <StepExplainerPanels explainer={explainer} measureKey={measureKey} />
    </View>
  );
}
